'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { logger } from '@/lib/logger';

export interface Auth0User {
  sub: string;
  email: string;
  name?: string;
  picture?: string;
  email_verified?: boolean;
  given_name?: string;
  family_name?: string;
  locale?: string;
  updated_at?: string;
}

export interface Auth0Session {
  user: Auth0User;
  accessToken?: string;
  idToken?: string;
  expiresAt?: number;
}

interface Auth0ContextType {
  session: Auth0Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const Auth0Context = createContext<Auth0ContextType | null>(null);

export function useAuth0() {
  const context = useContext(Auth0Context);
  if (!context) {
    throw new Error('useAuth0 must be used within an Auth0Provider');
  }
  return context;
}

export function Auth0Provider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Auth0Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshSession = async () => {
    try {
      setIsLoading(true);
      
      // Fetch session from our API endpoint
      const response = await fetch('/api/auth/session', {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const sessionData = await response.json();
        if (sessionData.user) {
          setSession(sessionData);
        } else {
          setSession(null);
        }
      } else {
        setSession(null);
      }
    } catch (error) {
      logger.error('Error refreshing Auth0 session:', error);
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async () => {
    try {
      // Redirect to Auth0 login
      window.location.href = '/api/auth/login';
    } catch (error) {
      logger.error('Error during Auth0 login:', error);
    }
  };

  const logout = async () => {
    try {
      // Call our logout endpoint
      await fetch('/api/auth/signout', {
        method: 'POST',
        credentials: 'include'
      });
      
      setSession(null);
      
      // Redirect to Auth0 logout
      window.location.href = '/api/auth/logout';
    } catch (error) {
      logger.error('Error during Auth0 logout:', error);
      // Still clear local session even if API call fails
      setSession(null);
      window.location.href = '/api/auth/logout';
    }
  };

  // Initialize session on mount
  useEffect(() => {
    refreshSession();
  }, []);

  // Set up periodic session refresh
  useEffect(() => {
    if (session?.expiresAt) {
      const now = Date.now();
      const expiresAt = session.expiresAt * 1000; // Convert to milliseconds
      const timeUntilExpiry = expiresAt - now;
      
      // Refresh 5 minutes before expiry
      const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 60 * 1000);
      
      if (refreshTime > 0) {
        const timeout = setTimeout(() => {
          refreshSession();
        }, refreshTime);
        
        return () => clearTimeout(timeout);
      }
    }
  }, [session]);

  const isAuthenticated = !!session?.user;

  const contextValue: Auth0ContextType = {
    session,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshSession
  };

  return (
    <Auth0Context.Provider value={contextValue}>
      {children}
    </Auth0Context.Provider>
  );
}